/**
 * Orders Client
 * Provides contract-based access to order management endpoints.
 * Follows Multi-Endpoint pattern from client factory architecture.
 * RAW-first model: All endpoints wired with RAW schemas for contract validation.
 * @module api/clients/orders
 */
import api from "@/services/api"
import { createContractClient } from "../contractClient"
import {
  // RAW schemas for contract validation (validated before normalization)
  UpdateOrderParamsResponseSchema,
  AssignManagerResponseSchema,
  MessageTemplateResponseSchema,
  SendMessageResponseSchema,
  AddTrackingNumberResponseSchema,
  DeliveryPriceQuoteResponseSchema,
  DeliveryPriceSaveResponseSchema,
  AddProductResponseSchema,
  CancelPromocodeResponseSchema,
  SaveOrderReadyStatusResponseSchema,
  OracleRecommendationsResponseSchema,
} from "../contracts/orders"
import {
  normalizeOrderDetailsResponse,
  normalizeUpdateOrderParamsResponse,
  normalizeAssignManagerResponse,
  normalizeMessageTemplateResponse,
  normalizeSendMessageResponse,
  normalizeAddTrackingNumberResponse,
  normalizePrintCDEKLabelResponse,
  normalizeDeliveryPriceQuoteResponse,
  normalizeDeliveryPriceSaveResponse,
  normalizeAddProductResponse,
  normalizeApplyPromocodeResponse,
  normalizeOracleRecommendationsResponse,
  normalizeSaveOrderReadyStatusResponse,
} from "../normalizers/orders.normalizer"

// LLM:METADATA
// :hierarchy: [Domain:Orders | API_Client | Factory]
// :relates-to: uses: "createContractClient", uses: "orders contracts (RAW)", uses: "orders normalizers", required-by: "OrderRow.vue", required-by: "useOrders.js composable"
// :rationale: "Provides type-safe order management API calls with RAW schema validation and response normalization for 14+ order endpoints. All endpoints now wired with RAW schemas for contract validation (Wave 1 complete). getOrderDetails includes fallback to orders/positions endpoint, but propagates invariant failures for empty products."
// :contract: pre: "api client initialized", post: "Client object with all order management methods", invariant: "All RAW responses validated against schemas before normalization, getOrderDetails throws invariant error if products array is empty after all fallbacks"
// :decision_cache: "Chose Multi-Endpoint pattern for orders due to high endpoint count (14+) and diverse operations. Wired RAW schemas for all 11 createContractClient calls to enable schema-first validation. Removed silent fallback that masked empty products invariant - now propagates OrderDetailsInvariantError [ORD-CLIENT-001, FIX-C]"
// :references: SPEC: "Phase 3B Client Factory Implementation", ARCH: "client-factory-architecture.md Pattern 2: Multi-Endpoint"
// :wave: "1 - All createContractClient calls now wired with RAW schemas", "2 - Fixed invariant error propagation for empty products"
// LLM:END

/**
 * Create orders client with contract validation
 * @returns {Object} Orders client with all order management methods
 */
export function createOrdersClient() {
  // Order management endpoints - RAW schema validates backend response before normalization
  const updateParamsClient = createContractClient(
    api,
    "update/order_params",
    UpdateOrderParamsResponseSchema,
    normalizeUpdateOrderParamsResponse
  )

  const assignManagerClient = createContractClient(
    api,
    "update/order_manager",
    AssignManagerResponseSchema,
    normalizeAssignManagerResponse
  )

  // Client communication endpoints - RAW schema validates response
  const messageTemplateClient = createContractClient(
    api,
    "custom/get_client_message",
    MessageTemplateResponseSchema,
    normalizeMessageTemplateResponse
  )

  const sendMessageClient = createContractClient(
    api,
    "custom/send_client_message",
    SendMessageResponseSchema,
    normalizeSendMessageResponse
  )

  // Tracking and delivery endpoints - RAW schema validates response
  const addTrackingClient = createContractClient(
    api,
    "send_order_tracking",
    AddTrackingNumberResponseSchema,
    normalizeAddTrackingNumberResponse
  )

  const deliveryPriceQuoteClient = createContractClient(
    api,
    "order_world_delivery_price",
    DeliveryPriceQuoteResponseSchema,
    normalizeDeliveryPriceQuoteResponse
  )

  const deliveryPriceSaveClient = createContractClient(
    api,
    "orders/change_delivery_price",
    DeliveryPriceSaveResponseSchema,
    normalizeDeliveryPriceSaveResponse
  )

  // Product and promocode endpoints - RAW schema validates response
  const addProductClient = createContractClient(
    api,
    "custom/save_more_products_for_order",
    AddProductResponseSchema,
    normalizeAddProductResponse
  )

  const cancelPromocodeClient = createContractClient(
    api,
    "custom/order_cancel_promocode",
    CancelPromocodeResponseSchema,
    normalizeApplyPromocodeResponse
  )

  // Save order ready status endpoint - RAW schema validates response
  const saveOrderReadyStatusClient = createContractClient(
    api,
    "custom/order_save_ready",
    SaveOrderReadyStatusResponseSchema,
    normalizeSaveOrderReadyStatusResponse
  )

  // Oracle recommendations endpoint - RAW schema validates response
  const oracleRecommendationsClient = createContractClient(
    api,
    "custom/save_order_oracle_response",
    OracleRecommendationsResponseSchema,
    normalizeOracleRecommendationsResponse
  )

  // CDEK print endpoint - uses direct api.get for simple passthrough
  // Note: Could be wired with schema if needed, but kept as direct call for simplicity

  return {
    /**
     * Get order details with strict invariant enforcement
     * Tries get_one_order endpoint first, falls back to orders/{id} + orders/positions.
     * Products array MUST be non-empty; throws OrderDetailsInvariantError if empty.
     *
     * @param {number} orderId - Order ID
     * @returns {Promise<Object>} Normalized order details with products
     * @throws {Error} Network/HTTP errors from API calls
     * @throws {OrderDetailsInvariantError} If products array is empty after all fallbacks
     *
     * @example
     * const orderDetails = await ordersClient.getOrderDetails(123)
     * console.log(orderDetails.order_info.m_item_client_name)
     * console.log(orderDetails.products)
     */
    getOrderDetails: async (orderId) => {
      try {
        // Try primary endpoint first
        const response = await api.get(`get_one_order/${orderId}`)
        return normalizeOrderDetailsResponse(response.data)
      } catch (primaryError) {
        // Fallback: combine orders/{id} and orders/positions (canonical endpoint)
        try {
          const [orderResponse, positionsResponse] = await Promise.all([
            api.get(`orders/${orderId}`),
            api.post("orders/positions", { order_id: orderId }),
          ])

          const positionsPayload = positionsResponse?.data
          let positions = Array.isArray(positionsPayload)
            ? positionsPayload
            : Array.isArray(positionsPayload?.items)
              ? positionsPayload.items
              : Array.isArray(positionsPayload?.data)
                ? positionsPayload.data
                : []

          // Extract positions from envelope variants if positions array is empty
          // Note: If this still results in empty products, normalizer will throw invariant error
          if (
            (!positions || positions.length === 0) &&
            orderResponse.data?.order_info?.variants
          ) {
            // Keep object-map variants intact: their keys are canonical rowids
            // consumed by the normalizer and later delete/edit requests.
            positions = orderResponse.data.order_info.variants
          }

          // Combine responses - normalizer enforces non-empty products invariant
          const combined = {
            ...orderResponse.data,
            products: positions,
          }

          return normalizeOrderDetailsResponse(combined)
        } catch (fallbackError) {
          // Propagate fallback errors directly (including invariant errors)
          // If fallback failed with invariant error, throw it; otherwise throw primary
          if (
            fallbackError?.name === "OrderDetailsInvariantError" ||
            fallbackError?.code?.includes("ORDER_PRODUCTS_EMPTY")
          ) {
            throw fallbackError
          }
          throw primaryError
        }
      }
    },

    /**
     * Update order parameters (status, payment, discount, etc.)
     * Supports mass updates for multiple orders
     *
     * @param {Object} params - Update parameters
     * @param {number} params.id - Order ID
     * @param {string} params.status - Order status
     * @param {string} params.paid - Payment status
     * @param {number} params.payment - Payment type ID
     * @param {string} params.comment - Admin comment
     * @param {string} params.discountFixed - Fixed discount amount
     * @param {string} params.discountPercent - Percentage discount
     * @param {string} params.certDiscount - Certificate discount
     * @param {boolean} params.feedback - Feedback check status
     * @param {string} params.role - User role for permission check
     * @param {number[]} [params.otherOrders] - Optional array of order IDs for mass update
     * @returns {Promise<Object>} Normalized update response
     *
     * @example
     * const result = await ordersClient.updateOrderParams({
     *   id: 123,
     *   status: '5',
     *   paid: '1',
     *   payment: 2,
     *   comment: 'Updated order',
     *   discountFixed: '0',
     *   discountPercent: '0',
     *   certDiscount: '0',
     *   feedback: false,
     *   role: 'admin'
     * })
     */
    updateOrderParams: async (params) => {
      return updateParamsClient.post(params)
    },

    /**
     * Assign manager to order
     *
     * @param {number} orderId - Order ID
     * @param {number} managerId - Manager ID from dictionary
     * @returns {Promise<Object>} Normalized assign response
     *
     * @example
     * const result = await ordersClient.assignManager(123, 5)
     * if (result.success) {
     *   console.log('Manager assigned')
     * }
     */
    assignManager: async (orderId, managerId) => {
      return assignManagerClient.post({
        id: orderId,
        manager: managerId,
      })
    },

    /**
     * Get message template for client communication
     * Returns pre-filled message based on order status
     *
     * @param {Object} params - Template parameters
     * @param {string} params.paid - Payment status
     * @param {boolean} params.delivery - Has delivery
     * @param {number} params.payment_type - Payment type ID
     * @returns {Promise<Object>} Normalized message template response
     * @returns {string} returns.message - Message template text
     *
     * @example
     * const { message } = await ordersClient.getMessageTemplates({
     *   paid: '0',
     *   delivery: true,
     *   payment_type: 2
     * })
     */
    getMessageTemplates: async (params) => {
      return messageTemplateClient.post(params)
    },

    /**
     * Send message to client
     *
     * @param {Object} params - Message parameters
     * @param {string} params.message - Message text
     * @param {number} params.order_id - Order ID
     * @param {string} params.client_email - Client email
     * @param {string} params.client_name - Client name
     * @returns {Promise<Object>} Normalized send response
     *
     * @example
     * const result = await ordersClient.sendMessage({
     *   message: 'Your order is ready',
     *   order_id: 123,
     *   client_email: 'user@example.com',
     *   client_name: 'Ivan'
     * })
     */
    sendMessage: async (params) => {
      return sendMessageClient.post(params)
    },

    /**
     * Add tracking number and send notification to client
     *
     * @param {Object} params - Tracking parameters
     * @param {number} params.order_id - Order ID
     * @param {string} params.email - Client email
     * @param {number} params.d_type - Delivery type ID
     * @param {string} params.num - Tracking number
     * @returns {Promise<Object>} Normalized tracking response
     * @returns {boolean} returns.success - Success indicator
     * @returns {string} [returns.error] - Error message if failed
     *
     * @example
     * const result = await ordersClient.addTrackingNumber({
     *   order_id: 123,
     *   email: 'user@example.com',
     *   d_type: 1,
     *   num: 'TRACK123456'
     * })
     */
    addTrackingNumber: async (params) => {
      return addTrackingClient.post(params)
    },

    /**
     * Print CDEK shipping label
     * Returns print URL or wait status if not ready
     *
     * @param {number} orderId - Order ID
     * @param {string} sdekOrderId - CDEK order ID
     * @returns {Promise<Object>} Normalized print response
     * @returns {string} returns.url - Print URL (if ready)
     * @returns {string} returns.wait - Wait message (if not ready)
     *
     * @example
     * const result = await ordersClient.printCDEKLabel(123, 'CDEK123')
     * if (result.url) {
     *   window.open(result.url, '_blank')
     * } else {
     *   console.log('Status:', result.wait)
     * }
     */
    printCDEKLabel: async (orderId, sdekOrderId) => {
      const response = await api.get(`sdek/print/${orderId}/${sdekOrderId}`)
      return normalizePrintCDEKLabelResponse(response.data)
    },

    /**
     * Create or reuse a Yandex PVZ delivery request through the canonical SPA API seam.
     *
     * @param {number} orderId - Order ID
     * @returns {Promise<{request_id: string, url: string}>} Canonical Yandex PVZ print payload
     */
    createYandexPvzOrder: async (orderId) => {
      const response = await api.post("print/yandex/pvz/create", {
        order_id: orderId,
      })

      return {
        request_id: response?.data?.request_id?.toString?.() || "",
        url: response?.data?.url?.toString?.() || "",
      }
    },

    /**
     * Quote world delivery price
     * Read-only calculation endpoint for international shipping.
     *
     * @param {Object} params - Quote parameters
     * @param {string|number} params.country - Destination country identifier
     * @param {number} params.weight - Package weight for quote calculation
     * @returns {Promise<Object>} Normalized quote response
     * @returns {number} returns.price - Calculated delivery quote
     *
     * @example
     * const result = await ordersClient.quoteWorldDeliveryPrice({
     *   country: 78,
     *   weight: 1.5
     * })
     */
    quoteWorldDeliveryPrice: async (params) => {
      return deliveryPriceQuoteClient.post(params)
    },

    /**
     * Persist delivery price on an existing order.
     * Uses dedicated mutation route instead of the quote endpoint.
     *
     * @param {Object} params - Save parameters
     * @param {number} params.order_id - Order ID
     * @param {number} params.delivery_price - Delivery price to persist
     * @returns {Promise<Object>} Normalized save response
     * @returns {boolean} returns.success - Success indicator
     *
     * @example
     * const result = await ordersClient.saveDeliveryPrice({
     *   order_id: 123,
     *   delivery_price: 2500
     * })
     */
    saveDeliveryPrice: async (params) => {
      return deliveryPriceSaveClient.post({
        order_id: params.order_id,
        delivery_price: params.delivery_price,
      })
    },

    /**
     * Add products to custom order
     *
     * @param {Object} params - Add product parameters
     * @param {number} params.orderID - Order ID
     * @param {Array} params.products - Array of product objects
     * @param {number} params.manager - Manager ID
     * @returns {Promise<Object>} Normalized add response
     *
     * @example
     * const result = await ordersClient.addProduct({
     *   orderID: 123,
     *   products: [{ m_item_id: 456, ... }],
     *   manager: 5
     * })
     */
    addProduct: async (params) => {
      return addProductClient.post({
        order_id: params.orderID, // ✅ Transform to snake_case
        products: params.products,
        manager: params.manager, // ✅ Pass manager to backend
      })
    },

    /**
     * Cancel promocode for order
     * Removes promocode and resets discounts
     *
     * @param {number} orderId - Order ID
     * @returns {Promise<Object>} Normalized cancel response
     *
     * @example
     * const result = await ordersClient.cancelPromocode(123)
     */
    cancelPromocode: async (orderId) => {
      return cancelPromocodeClient.post({
        order_id: orderId,
      })
    },

    /**
     * Save order ready status for market orders
     * Marks order as ready/collected
     *
     * @param {Object} params - Save ready status parameters
     * @param {number} params.order_id - Order ID
     * @param {string} params.status - Ready status ('1' for ready, '0' for not ready)
     * @returns {Promise<Object>} Normalized save response
     * @returns {boolean} returns.success - Success indicator
     *
     * @example
     * const result = await ordersClient.saveOrderReadyStatus({
     *   order_id: 123,
     *   status: '1'
     * })
     * if (result.success) {
     *   console.log('Order marked as ready')
     * }
     */
    saveOrderReadyStatus: async (params) => {
      return saveOrderReadyStatusClient.post({
        order_id: params.order_id,
        status: params.status,
      })
    },

    /**
     * Save oracle recommendations and send to client
     * Used for personalized product recommendations
     *
     * @param {Object} params - Oracle recommendations parameters
     * @param {number} params.orderID - Order ID
     * @param {string} params.email - Client email
     * @param {string} params.name - Client short name
     * @param {number} params.manager - Assigned manager ID
     * @param {Array} params.variants - Array of recommended product IDs
     * @returns {Promise<Object>} Normalized oracle response
     *
     * @example
     * const result = await ordersClient.getOracleRecommendations({
     *   orderID: 123,
     *   email: 'user@example.com',
     *   name: 'Ivan',
     *   manager: 5,
     *   variants: ['lp:456', 'cd:789']
     * })
     */
    getOracleRecommendations: async (params) => {
      return oracleRecommendationsClient.post(params)
    },

    /**
     * Get store pickup report data
     * Returns list of items available for store pickup
     *
     * @param {Object} params - Report parameters
     * @param {number} params.takeFromStock - Report type flag (1 for store pickup)
     * @param {number} params.num - Report number (0 for current, >0 for historical)
     * @param {number} [params.save] - Save flag (1 for download as file, 0 or undefined for display)
     * @returns {Promise<Object>} Response data with items array or blob for download
     *
     * @example
     * // Get current report data
     * const result = await ordersClient.getStorePickupData({
     *   takeFromStock: 1,
     *   num: 0
     * })
     *
     * @example
     * // Download historical report as file
     * const blob = await ordersClient.getStorePickupData({
     *   takeFromStock: 1,
     *   num: 5,
     *   save: 1
     * }, { responseType: 'blob' })
     */
    getStorePickupData: async (params, config = {}) => {
      const { save = 0 } = params
      const requestConfig = {
        ...config,
        headers: {
          "Content-Type": "multipart/form-data",
          ...config.headers,
        },
        responseType: save === 1 ? "blob" : "json",
      }
      const response = await api.post("edit/newreports", params, requestConfig)
      return response.data
    },

    /**
     * Get cart details by cart ID
     * Returns full cart data including products and user info
     *
     * @param {number} cartId - Cart ID
     * @returns {Promise<Object>} Cart data with products and user info
     *
     * @example
     * const cartData = await ordersClient.getCart(123)
     * console.log(cartData.products)
     * console.log(cartData.user_email)
     */
    getCart: async (cartId) => {
      const response = await api.get(`custom/carts/${cartId}`)
      return response.data
    },
  }
}

// Singleton instance for direct import
export const ordersClient = createOrdersClient()
